import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { BroadcastersParams } from '../queries/broadcasters';
import { getBroadcastersQuery } from '../queries/broadcasters';

import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import { dateToString } from '@utilities/dateTime';

const useMatchBroadcasters = (
  match: Match,
  options: {
    broadcasters?: BroadcastersParams;
  },
  enabled: boolean,
) => {
  const broadcastersOpt = useMemo<BroadcastersParams>(() => {
    if (
      !options.broadcasters ||
      !match?.kickOffTime ||
      ![MatchStatus.UPCOMING, MatchStatus.LIVE].includes(match?.status) ||
      (match?.status === MatchStatus.LIVE && match?.streamUrl)
    ) {
      return null;
    }

    const date = dateToString(match.kickOffTime?.dateTime ?? match.kickOffTime?.date);

    return {
      ...options.broadcasters,
      startDate: date,
      endDate: date,
    };
  }, [match?.kickOffTime, match?.status, match?.streamUrl, options.broadcasters]);

  const loadBroadcasters = enabled && !!broadcastersOpt;

  const { data: broadcasters } = useQuery({
    ...getBroadcastersQuery(broadcastersOpt),
    select: (result) => result?.find((b) => b.matchId === match.id)?.broadcasters.filter((b) => !!b.name) ?? [],
    placeholderData: [],
    enabled: !!loadBroadcasters,
  });

  return loadBroadcasters ? broadcasters : [];
};

export default useMatchBroadcasters;
